/**
 * Column Filter Popover - Multi-select dropdown for filtering table columns
 *
 * Shows unique values for a column with checkboxes.
 * Includes search field when >15 unique values.
 */
import React, { useState, useMemo } from 'react'
import {
  Popover,
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Divider,
} from '@mui/material'
import { ColumnDef } from '../reports/BaseReportPage'

const SEARCH_THRESHOLD = 15
const MAX_HEIGHT = 300

interface ColumnFilterPopoverProps {
  anchorEl: HTMLElement | null
  column: ColumnDef
  allValues: string[]
  selectedValues: string[]
  formatValue: (value: any, col: ColumnDef) => string
  onApply: (columnKey: string, values: string[]) => void
  onClose: () => void
}

export default function ColumnFilterPopover({
  anchorEl,
  column,
  allValues,
  selectedValues,
  formatValue,
  onApply,
  onClose,
}: ColumnFilterPopoverProps) {
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set(selectedValues))

  const uniqueValues = useMemo(() => {
    const sorted = [...allValues].sort((a, b) => {
      const fa = formatValue(a, column)
      const fb = formatValue(b, column)
      return fa.localeCompare(fb)
    })
    return sorted
  }, [allValues, column, formatValue])

  const filteredValues = useMemo(() => {
    if (!search) return uniqueValues
    const lower = search.toLowerCase()
    return uniqueValues.filter(v =>
      formatValue(v, column).toLowerCase().includes(lower)
    )
  }, [uniqueValues, search, column, formatValue])

  const handleToggle = (value: string) => {
    const next = new Set(selected)
    if (next.has(value)) {
      next.delete(value)
    } else {
      next.add(value)
    }
    setSelected(next)
  }

  const handleSelectAll = () => {
    setSelected(new Set(filteredValues))
  }

  const handleClear = () => {
    setSelected(new Set())
  }

  const handleApply = () => {
    onApply(column.key, Array.from(selected))
    onClose()
  }

  const allFilteredSelected = filteredValues.length > 0 && filteredValues.every(v => selected.has(v))

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{ paper: { sx: { width: 260, maxHeight: MAX_HEIGHT + 140 } } }}
    >
      <Box sx={{ p: 1.5 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {column.label}
        </Typography>

        {uniqueValues.length > SEARCH_THRESHOLD && (
          <TextField
            size="small"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            sx={{ mb: 1 }}
            autoFocus
          />
        )}

        <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
          <Button size="small" onClick={allFilteredSelected ? handleClear : handleSelectAll}>
            {allFilteredSelected ? 'Clear' : 'Select All'}
          </Button>
        </Box>

        <Divider sx={{ mb: 0.5 }} />

        <Box sx={{ maxHeight: MAX_HEIGHT, overflow: 'auto' }}>
          {filteredValues.map((value) => (
            <FormControlLabel
              key={value}
              control={
                <Checkbox
                  checked={selected.has(value)}
                  onChange={() => handleToggle(value)}
                  size="small"
                  sx={{ py: 0.25, color: '#007f98', '&.Mui-checked': { color: '#007f98' } }}
                />
              }
              label={
                <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                  {formatValue(value, column)}
                </Typography>
              }
              sx={{ display: 'flex', ml: 0, width: '100%' }}
            />
          ))}
          {filteredValues.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1, textAlign: 'center' }}>
              No matching values
            </Typography>
          )}
        </Box>

        <Divider sx={{ mt: 0.5 }} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
          <Button size="small" onClick={onClose}>
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={handleApply} sx={{ backgroundColor: '#007f98' }}>
            Apply
          </Button>
        </Box>
      </Box>
    </Popover>
  )
}
